import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';

export interface N58SslStatus {
  valid: boolean;
  expiresAt: string | null;
  daysRemaining: number | null;
  blocked: boolean;
  retrying: boolean;
  lastCheckAt: string | null;
}

export interface N58Health {
  operational: boolean;
  ssl: N58SslStatus;
  session: {
    active: boolean;
    token: boolean;
  };
  config: {
    loaded: boolean;
    baseUrl: string;
  };
  timestamp: string;
}

/**
 * Un caso de pago N58 que necesita una decision: el banco devolvio la plata, o
 * el pago nunca aparecio en la cartola. Lo abre el vigilante del backend; el
 * operador lo cierra.
 */
export interface N58CasoAbierto {
  id: number;
  referencia: string;
  transaccionId: number;
  usuarioId: number;
  /**
   * Por que se abrio el caso. Espeja `N58IncidenteTipo` del backend.
   *
   * `SIN_RESPALDO`: N58 dijo que el pago salio y la cartola nunca trajo el
   * renglon. El banco cobro la comision y el beneficiario no cobro.
   * `REINTEGRO`: el pago salio y despues el banco lo devolvio.
   * `SIN_WEBHOOK`: el renglon esta en la cartola —o sea salio— pero el banco
   * nunca mando la notificacion de liquidacion. Es sospecha, no prueba: es el
   * unico tipo que se reclasifica solo cuando aparece la evidencia.
   * `RECHAZO_BANCO`: el webhook llego diciendo rechazado y nuestro log quedo
   * en exito.
   *
   * Estan los cuatro aunque el front solo pinte titulo y color: si el backend
   * agrega uno y aca no esta, el compilador avisa. Antes la union tenia dos y
   * los otros dos entraban sin que nada se quejara, con el titulo equivocado.
   */
  tipo: 'SIN_RESPALDO' | 'REINTEGRO' | 'SIN_WEBHOOK' | 'RECHAZO_BANCO';
  estado: string;
  montoVes: string | number | null;
  montoOrigen: string | number | null;
  monedaOrigen: string | null;
  emitidoEn: string | null;
  detectadoEn: string | null;
  cliente: string | null;
  emailCliente: string | null;
  beneficiario: string | null;
  cuentaBeneficiario: string | null;
  evidencia: string | null;
  movimientoReposicionId: number | null;
  tieneWebhook: boolean;
  tieneRenglon: boolean;
  /**
   * El webhook del banco dice que el pago se liquido. Con `tieneRenglon` es la
   * prueba de que el beneficiario cobro. Opcional: un backend sin desplegar no
   * lo manda, y ahi el modal simplemente no muestra la señal.
   */
  liquidado?: boolean;
  yaAbonado: boolean;
  /** El backend ya decidio si el boton de reversar va o no, y por que. */
  reversable: boolean;
  motivoNoReversable: string | null;
}

export interface N58ToggleError {
  status: number;
  message: string;
  /**
   * Legacy: el backend retornaba este campo cuando la regla de exclusividad
   * N58 ⊕ BP rechazaba el toggle. Desde el rediseño F2 (2026-05) los toggles
   * son independientes y este campo queda como dead code defensivo.
   */
  conflictWith?: 'PAGO_N58' | 'PAGO_BANCO_PLAZA';
}

/**
 * Respuesta de `GET n58/movements/:id`. Espejo de `N58MovementDetail` en el
 * backend. Todo bloque es opcional: un movimiento viejo no tiene webhook, un
 * payin no tiene beneficiario, y un cargo del banco no tiene ninguno de los
 * dos. La vista tiene que aguantar que falten.
 */
export interface N58MovementDetail {
  movement: any;
  contraparte: {
    bancoCodigo: string | null;
    cuenta: string | null;
    nombre: string | null;
  } | null;
  cliente: {
    usuarioId: number;
    nombre: string | null;
    apellidos: string | null;
    email: string | null;
    documento: string | null;
    paisResidencia: string | null;
  } | null;
  beneficiario: {
    nombre: string | null;
    documento: string | null;
    cuenta: string | null;
    bancoNombre: string | null;
    bancoCodigo: string | null;
    esPagoMovil: boolean;
  } | null;
  transaccion: {
    id: number;
    estado: string | null;
    referencia: string | null;
    montoOrigen: number | null;
    monedaOrigen: string | null;
    montoDestino: number | null;
    monedaDestino: string | null;
    operador: string | null;
    createdAt: string | null;
  } | null;
  payoutLog: {
    id: number;
    status: string;
    referencia: string | null;
    cuentaDestino: string | null;
    operadorEmail: string | null;
    comision: string | null;
    saldoDespues: string | null;
    errorCode: string | null;
    errorDetail: string | null;
    completedAt: string | null;
  } | null;
  bancoDice: {
    estado: string | null;
    motivo: string | null;
    ordenanteNombre: string | null;
    ordenanteDocumento: string | null;
    ordenanteCuenta: string | null;
    ordenanteBanco: string | null;
    beneficiarioNombre: string | null;
    beneficiarioDocumento: string | null;
    beneficiarioCuenta: string | null;
    beneficiarioBanco: string | null;
    concepto: string | null;
    recibidoEl: string | null;
  } | null;
  referenciaVes: {
    id: string;
    titular: string | null;
    banco: string | null;
    cuenta: string | null;
    tipoCuenta: string | null;
    monto: number | null;
    status: string | null;
    operatorEmail: string | null;
  } | null;
}

@Injectable({
  providedIn: 'root',
})
export class N58Service {
  private baseUrl = environment.apiNest;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session?.accessToken?.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${session.accessToken.jwtToken.trim()}`);
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error en el servicio N58';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = error.error?.message || `Error del servidor: ${error.status}`;
    }
    return throwError(() => errorMessage);
  }

  getHealth(): Observable<N58Health> {
    const url = `${this.baseUrl}n58/health`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<N58Health>(url, { headers })),
      catchError(this.handleError),
    );
  }

  getProducts(productType: string): Observable<any> {
    const url = `${this.baseUrl}n58/products/${productType}`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<any>(url, { headers })),
      catchError(this.handleError),
    );
  }

  getMovements(params: Record<string, string>): Observable<any> {
    const url = `${this.baseUrl}n58/movements`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<any>(url, { headers, params })),
      catchError(this.handleError),
    );
  }

  /**
   * Detalle de un movimiento con la contraparte identificada: cliente,
   * beneficiario y la version del banco.
   *
   * El listado no trae nada de eso — el backend lo cruza al leer contra
   * `n58_transaction_logs`, `transacciones` y `n58_webhook_events`, asi que
   * hace falta este segundo viaje. Todos los bloques pueden venir en null.
   */
  getMovement(id: number): Observable<N58MovementDetail> {
    const url = `${this.baseUrl}n58/movements/${id}`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<N58MovementDetail>(url, { headers })),
      catchError(this.handleError),
    );
  }

  /**
   * Descarga movimientos N58 filtrados como blob XLSX.
   * El backend aplica `AguacateOperatorGuard`. Si el conjunto excede
   * 50000 filas → 413 PayloadTooLarge.
   */
  exportMovements(params: Record<string, string>): Observable<Blob> {
    const url = `${this.baseUrl}n58/movements/export`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get(url, {
          headers,
          params,
          responseType: 'blob',
        }),
      ),
      catchError(this.handleError),
    );
  }

  getMovementStats(): Observable<any> {
    const url = `${this.baseUrl}n58/movements/stats`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<any>(url, { headers })),
      catchError(this.handleError),
    );
  }

  forceSync(): Observable<any> {
    const url = `${this.baseUrl}n58/movements/sync`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<any>(url, {}, { headers })),
      catchError(this.handleError),
    );
  }

  getPagoN58Status(): Observable<{ success: boolean; active: boolean }> {
    const url = `${this.baseUrl}n58/pago-n58/status`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ success: boolean; active: boolean }>(url, { headers })),
      catchError(this.handleError),
    );
  }

  togglePagoN58(): Observable<{ success: boolean; active: boolean }> {
    const url = `${this.baseUrl}n58/pago-n58/toggle`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<{ success: boolean; active: boolean }>(url, {}, { headers }),
      ),
      catchError((error: HttpErrorResponse) => {
        const payload = error.error ?? {};
        const msg =
          typeof payload === 'object' && payload.message
            ? payload.message
            : `Error del servidor: ${error.status}`;
        const out: N58ToggleError = {
          status: error.status,
          message: msg,
          conflictWith: payload.conflictWith,
        };
        return throwError(() => out);
      }),
    );
  }

  getMontoMin(): Observable<{ success: boolean; valor: number | null }> {
    const url = `${this.baseUrl}n58/monto-min`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ success: boolean; valor: number | null }>(url, { headers })),
      catchError(this.handleError),
    );
  }

  updateMontoMin(valor: number): Observable<{ success: boolean; valor: number }> {
    const url = `${this.baseUrl}n58/monto-min`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<{ success: boolean; valor: number }>(url, { valor }, { headers })),
      catchError(this.handleError),
    );
  }

  validateCryptoWallet(userId: number, wallet: string): Observable<any> {
    const url = `${this.baseUrl}validate-crypto-wallet`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<any>(url, { userId, wallet }, { headers })),
      catchError(this.handleError),
    );
  }

  // ─── Casos de pago (payout sin respaldo / reintegro) ──────────────
  //
  // Las rutas viven en `wallet-ves` y no en `n58` porque del lado del backend
  // el modulo de VES ya importa el de N58 y al reves hacia falta un forwardRef.

  listarCasos(
    estado: 'ABIERTO' | 'REPUESTO_A_MANO' | 'REVERSADO_A_CLIENTE' | 'DESCARTADO' = 'ABIERTO',
  ): Observable<{ success: boolean; data: N58CasoAbierto[] }> {
    const url = `${this.baseUrl}wallet-ves/payout-incidentes`;
    const params = new HttpParams().set('estado', estado);
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<{ success: boolean; data: N58CasoAbierto[] }>(url, { headers, params }),
      ),
      catchError(this.handleError),
    );
  }

  /**
   * Devuelve el saldo al cliente.
   *
   * `userId` viaja aunque el backend ya sepa de quien es el caso: el limitador
   * de operaciones manuales lo busca en el body y, si no lo encuentra, deja
   * pasar sin limitar.
   */
  reversarCaso(
    id: number,
    userId: number,
    nota: string,
  ): Observable<any> {
    const url = `${this.baseUrl}wallet-ves/payout-incidentes/${id}/reversar`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<any>(
          url,
          { userId, confirmadoConBanco: true, nota },
          { headers },
        ),
      ),
      catchError(this.handleError),
    );
  }

  /** El operador ya le pago al beneficiario desde el banco. No mueve plata. */
  marcarRepuestoAMano(id: number, nota: string): Observable<any> {
    const url = `${this.baseUrl}wallet-ves/payout-incidentes/${id}/repuesto-a-mano`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<any>(url, { nota }, { headers })),
      catchError(this.handleError),
    );
  }

  /** Era ruido. El backend exige motivo. */
  descartarCaso(id: number, nota: string): Observable<any> {
    const url = `${this.baseUrl}wallet-ves/payout-incidentes/${id}/descartar`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<any>(url, { nota }, { headers })),
      catchError(this.handleError),
    );
  }
}
